import { clearDraftV1, copyApprovedV1, loadNewDraftV1 } from 'client/actions/DocumentsV1/draftV1/draftV1Actions'
import { saveDraftThunkV1, updateApprovedThunkV1 } from 'client/actions/DocumentsV1/draftV1/draftV1CreateUpdateThunks'
import { loadApprovedThunkV1, loadDraftThunkV1 } from 'client/actions/DocumentsV1/draftV1/draftV1InitializationThunks'
import { fetchPreviewForDraftV1, setNewCustomerV1Thunk } from 'client/actions/DocumentsV1/draftV1/draftV1Thunks'
import { defaultLine } from 'client/actions/helpers/draft'
import { getSummary } from 'client/actions/helpers/receipt'
import { IThunkAction } from 'client/actions/index'
import { navigateAddModal, navigateRemoveModal, setNavigationPath } from 'client/actions/navigation'
import { hideGeneratingDocumentSpinner, showGeneratingDocumentSpinner } from 'client/actions/ongoing'
import * as documentApi from 'client/api/document'
import * as api from 'client/api/draft'
import { getNextNumberV1, ReceiptNumberResponseInterface } from 'client/api/invoiceNumber'
import * as receiptApi from 'client/api/receipt'
import { DeliverynoteDraftInterface } from 'client/components/DocumentsV1/Deliverynote/DeliverynoteInterface'
import { ReceiptDraftInterface, ReceiptType } from 'client/components/DocumentsV1/Receipt/ReceiptInterface'
import { IGraphQl } from 'client/graphQueries/IGraphQl'
import { downloadPdfByIdV1 } from 'client/hooks/useDownloadPdf'
import { DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'
import { ConfigInterface, Language } from 'client/Interfaces/Draft/v1/DraftInterface'
import { EditedDraftV1Interface } from 'client/Interfaces/Draft/v1/EditedDraftV1Interface'
import { ModalInterface } from 'client/Interfaces/ModalInterface'
import { StateInterface } from 'client/Interfaces/StateInterface'
import { getDraftV1Selector } from 'client/reducers/draftV1Reducer'
import { settingsSelector, tradeNameV1Selector } from 'client/reducers/settings'
import moment from 'moment'

export const createOrUpdateReceiptDraftThenNavigateToListV1 = (): IThunkAction => {
  return async (dispatch, getState: () => StateInterface) => {
    try {
      dispatch(showGeneratingDocumentSpinner())
      await dispatch(saveDraftThunkV1(DocumentTypes.RECEIPT))
      const draft = getDraftV1Selector(getState())
      if (!draft.invalidFields && draft.id) {
        dispatch(clearDraftV1())
        dispatch(setNavigationPath(`/receipts/drafts/${draft.id}`))
      }
    } catch (e) {
      console.error('Error occurred while saving draft', e)
      dispatch(setNavigationPath('/receipts'))
    } finally {
      dispatch(hideGeneratingDocumentSpinner())
    }
  }
}

export const approveReceiptDraft = (): IThunkAction<null, string | ReceiptDraftInterface, Promise<void>> => {
  return async (dispatch, getState) => {
    await dispatch(saveDraftThunkV1(DocumentTypes.RECEIPT))
    const draft = getDraftV1Selector(getState())
    const document = await api.approveDraftV1(DocumentTypes.RECEIPT, draft.id)
    if (!draft.invalidFields && draft.id) {
      await dispatch(loadApprovedThunkV1(DocumentTypes.RECEIPT, document.id))
    }
  }
}

export const approveDraftOrUpdateReceiptThenNavigateToListV1 = (downloadPdf = false): IThunkAction => {
  return async (dispatch, getState): Promise<void> => {
    try {
      dispatch(showGeneratingDocumentSpinner())
      let draft = getDraftV1Selector(getState())
      if (draft && draft.documentType === ReceiptType.approved) {
        await dispatch(updateApprovedThunkV1(DocumentTypes.RECEIPT))
      } else {
        await dispatch(approveReceiptDraft())
      }
      draft = getDraftV1Selector(getState())
      if (draft.id) {
        if (downloadPdf) {
          const state = getState()
          const { token, tradeNames } = {
            token: state.token,
            tradeNames: settingsSelector(state).values.company.tradeNames,
          }
          await downloadPdfByIdV1(DocumentTypes.RECEIPT, draft.id, token, tradeNames)
        }
        dispatch(setNavigationPath(`/receipts/approved/${draft.id}`))
      }
    } catch (e) {
      console.error('Error occurred while approving or editing document', e)
      dispatch(setNavigationPath('/receipts'))
    } finally {
      dispatch(hideGeneratingDocumentSpinner())
      dispatch(clearDraftV1())
    }
  }
}

export const showReceiptDraft = (id: number): IThunkAction<null, ModalInterface | string> => {
  return (dispatch) => {
    dispatch(
      navigateAddModal({
        component: 'ShowDocumentModal',
        props: {
          content: documentApi.getDraftDocumentHTMLV1(DocumentTypes.RECEIPT, id),
          onClose: () => dispatch(navigateRemoveModal('ShowDocumentModal')),
        },
      }),
    )
  }
}

export const showReceipt = (id: number): IThunkAction<null, ModalInterface | string> => {
  return (dispatch) => {
    dispatch(
      navigateAddModal({
        component: 'ShowDocumentModal',
        props: {
          content: documentApi.getDocumentHTMLV1(DocumentTypes.RECEIPT, id),
          onClose: () => dispatch(navigateRemoveModal('ShowDocumentModal')),
        },
      }),
    )
  }
}

export const previewAndSendReceipt = (id: number): IThunkAction => {
  return async (dispatch) => {
    await dispatch(loadDraftThunkV1(DocumentTypes.RECEIPT, id))
    await dispatch(fetchPreviewForDraftV1(DocumentTypes.RECEIPT))
  }
}

export const copyReceipt = (
  id: number,
): IThunkAction<null, ReceiptDraftInterface | DeliverynoteDraftInterface | string> => {
  return async (dispatch) => {
    const copiedApproved = await receiptApi.getReceipt(id)
    delete copiedApproved.id
    delete copiedApproved.revisionId
    delete copiedApproved.status
    const { number: receiptNumber } = await getNextNumberV1<ReceiptNumberResponseInterface>(DocumentTypes.RECEIPT)
    dispatch(
      copyApprovedV1({
        ...copiedApproved,
        documentType: ReceiptType.draft,
        receiptNumber: receiptNumber.toString(),
      }),
    )
    dispatch(setNewCustomerV1Thunk(copiedApproved?.customer))
    dispatch(setNavigationPath('/editor/receipts/drafts/new'))
  }
}

export const loadNewReceiptDraftThunk = (
  config: IGraphQl<ConfigInterface>,
): IThunkAction<null, EditedDraftV1Interface, Promise<void>> => {
  return async (dispatch, getState) => {
    const { fields } = config
    const { title, ...fieldsForSettings } = fields
    const { columns } = fieldsForSettings
    const newColumnsValue = columns?.value?.map((column) => (column === 'total' ? 'grossTotal' : column))
    const configForSettings = { ...config, fields: { ...fieldsForSettings } }
    const timeZone = { name: 'timeZone', visible: true, value: Intl.DateTimeFormat().resolvedOptions().timeZone }
    configForSettings.fields.columns = { visible: columns.visible, value: newColumnsValue }
    configForSettings.fields.timeZone = timeZone
    const { settings, company: companyWithoutTradename } = getState()
    const summary = getSummary(config)
    const company = tradeNameV1Selector(getState(), fields.sender && fields.sender.value)
    const { number: receiptNumber } = await getNextNumberV1<ReceiptNumberResponseInterface>(DocumentTypes.RECEIPT)

    const draft = {
      receiptNumber,
      company,
      config: configForSettings,
      customer: null,
      summary,
      documentType: ReceiptType.draft,
      description: config.fields.description.value,
      footerText: config.fields.footerText.value,
      title: title.value,
      lines: [defaultLine(settings)],
      language: companyWithoutTradename.language as Language,
      issueDate: moment().toISOString(),
    }
    dispatch(loadNewDraftV1(draft))
  }
}
